import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Select, Stat, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { money, relativeTime } from '@/lib/format';
import { parseCsv } from '@/lib/csv';
import {
  commitImport, discardImport, editImportRow, getImport, getImportOwners, listImports, listOptions, reassignImportOwners, retryImportAi, setImportOwner,
  undoImport, uploadLegacyImport,
} from '@/data/client';
import { useMembers } from '@/data/hooks';
import type { ImportBatch, ImportDetail, ImportOverrides, ImportPlan, Lifecycle } from '@/data/types';
import { FormError, Modal } from '@/components/crm/Modal';
import { LIFECYCLE_LABEL, LIFECYCLE_OPTIONS } from '@/components/crm/LifecycleBadge';

const STATUS_LABEL: Record<ImportBatch['status'], string> = {
  analyzing: 'Reading notes', review: 'Ready to review', importing: 'Importing', done: 'Imported', undone: 'Undone',
};

/* Deals from the old CRM (its "Export deals" CSV): upload → AI reads the notes →
   review every suggestion → import → undo if needed. Nothing outside the review
   is written until "Import". */
export function LegacyImport() {
  const qc = useQueryClient();
  const { data: batches = [] } = useQuery({ queryKey: ['imports'], queryFn: listImports });
  const [openId, setOpenId] = useState<string | null>(null);
  const active = openId ?? batches.find((b) => b.status === 'analyzing' || b.status === 'review' || b.status === 'importing')?.id ?? null;
  const [uploadErrors, setUploadErrors] = useState<{ row: number; message: string }[]>([]);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { headers, rows } = parseCsv(await file.text());
      const objs = rows.map((cells) => Object.fromEntries(headers.map((h, i) => [h, cells[i] ?? ''])));
      return uploadLegacyImport({ fileName: file.name, headers, rows: objs });
    },
    onSuccess: (r) => { setUploadErrors(r.errors); setOpenId(r.batchId); void qc.invalidateQueries({ queryKey: ['imports'] }); },
  });

  return (
    <div className="crm-list">
      <Card padding={false} className="crm-section">
        <div className="crm-section__head"><h3>Import deals from the old CRM</h3></div>
        <p className="crm-cell-muted">
          Upload the deals export (CSV). Open, won and lost deals come in with their real dates, stage, value and every dated note. Companies are
          matched to the ones clientIQ already has; AI reads the notes to suggest follow-up dates, lost reasons and contacts. You review all of it
          before anything is saved, and an import can be undone.
        </p>
        <div className="crm-import-upload">
          <input type="file" accept=".csv,text/csv" disabled={upload.isPending}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
          {upload.isPending && <span className="crm-cell-sub">Uploading…</span>}
        </div>
        <FormError error={upload.error} />
        {uploadErrors.length > 0 && (
          <div className="crm-callout" style={{ marginTop: 12 }}>
            <strong>{uploadErrors.length} row(s) could not be read and were left out.</strong>
            <ul className="crm-import-notes">{uploadErrors.map((e) => <li key={e.row}>Row {e.row}: {e.message}</li>)}</ul>
          </div>
        )}
        {batches.length > 0 && (
          <table className="dash-table" style={{ marginTop: 16 }}>
            <thead><tr><th>File</th><th>Status</th><th>Started</th><th className="crm-num">Deals</th><th /></tr></thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className={b.id === active ? 'is-selected' : ''}>
                  <td>{b.fileName ?? 'Import'}</td>
                  <td><Badge variant={b.status === 'done' ? 'success' : b.status === 'undone' ? 'outline' : 'accent'}>{STATUS_LABEL[b.status]}</Badge></td>
                  <td className="crm-cell-muted">{relativeTime(b.createdAt)}</td>
                  <td className="crm-num">{b.result?.deals ?? '—'}</td>
                  <td className="crm-num"><Button size="sm" variant="ghost" onClick={() => setOpenId(b.id)}>Open</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {active && <BatchView key={active} id={active} onGone={() => setOpenId(null)} />}
    </div>
  );
}

function BatchView({ id, onGone }: { id: string; onGone: () => void }) {
  const qc = useQueryClient();
  // Light poll while background work runs; the full plan only in review/done.
  const { data: light } = useQuery({
    queryKey: ['import', id], queryFn: () => getImport(id, false),
    refetchInterval: (q) => (['analyzing', 'importing'].includes(q.state.data?.batch.status ?? '') ? 2500 : false),
  });
  const status = light?.batch.status;
  const { data: full } = useQuery({
    queryKey: ['import', id, 'rows', status], queryFn: () => getImport(id, true),
    enabled: status === 'review' || status === 'done',
  });
  const refreshAll = () => { for (const k of ['import', 'imports']) void qc.invalidateQueries({ queryKey: [k] }); };
  useEffect(() => { if (status === 'done' || status === 'undone') for (const k of ['deals', 'companies', 'contacts', 'followUps', 'imports']) void qc.invalidateQueries({ queryKey: [k] }); }, [status, qc]);
  if (!light) return null;
  const { batch, progress } = light;

  if (batch.status === 'analyzing') {
    const read = progress.total - progress.aiPending;
    return (
      <Card padding={false} className="crm-section">
        <div className="crm-section__head"><h3>{batch.fileName}</h3><Badge variant="accent">Reading notes</Badge></div>
        <p>AI is reading each deal's notes for follow-up dates, lost reasons and contacts — {read} of {progress.total} deals.</p>
        <progress className="crm-import-progress" max={progress.total} value={read} />
        <DefaultOwner batch={batch} onDone={refreshAll} />
      </Card>
    );
  }
  if (batch.status === 'importing') {
    const doneRows = progress.committed + progress.failed;
    const toWrite = progress.total - progress.skipped;
    return (
      <Card padding={false} className="crm-section">
        <div className="crm-section__head"><h3>{batch.fileName}</h3><Badge variant="accent">Importing</Badge></div>
        <p>Writing deals, companies, contacts and notes — {doneRows} of {toWrite}.</p>
        <progress className="crm-import-progress" max={toWrite} value={doneRows} />
      </Card>
    );
  }
  if (!full?.rows || !full.summary) return <Card padding={false} className="crm-section"><p className="crm-cell-muted">Loading…</p></Card>;
  if (batch.status === 'review') return <Review data={full} onChanged={refreshAll} onGone={onGone} />;
  return <Finished data={full} onChanged={refreshAll} />;
}

function DefaultOwner({ batch, onDone }: { batch: ImportBatch; onDone: () => void }) {
  const { ownerOptions } = useMembers();
  const set = useMutation({ mutationFn: (u: string) => setImportOwner(batch.id, u), onSuccess: onDone });
  return (
    <div className="crm-grid2" style={{ marginTop: 12 }}>
      <Select label="Owner for deals whose old owner isn't a clientIQ member" options={ownerOptions} value={batch.defaultOwnerUserId ?? ''}
        onChange={(e) => set.mutate(e.target.value)} />
      <p className="crm-cell-sub" style={{ alignSelf: 'end' }}>Deals owned by a member with the same name go to that member. The old owner's name is kept on the deal.</p>
      <FormError error={set.error} />
    </div>
  );
}

/* ---- review ------------------------------------------------------------------ */

type Section = 'matches' | 'newWon' | 'values' | 'websites' | 'skipped' | 'all';

function Review({ data, onChanged, onGone }: { data: ImportDetail; onChanged: () => void; onGone: () => void }) {
  const { batch, progress } = data;
  const rows = data.rows!;
  const s = data.summary!;
  const [section, setSection] = useState<Section>(s.undecidedMatches ? 'matches' : s.newWonCompanies ? 'newWon' : 'all');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const edit = useMutation({ mutationFn: (v: { rowId: string; o: ImportOverrides }) => editImportRow(batch.id, v.rowId, v.o), onSuccess: onChanged });
  const commit = useMutation({ mutationFn: () => commitImport(batch.id), onSuccess: onChanged });
  const retry = useMutation({ mutationFn: () => retryImportAi(batch.id), onSuccess: onChanged });
  const discard = useMutation({ mutationFn: () => discardImport(batch.id), onSuccess: () => { onChanged(); onGone(); } });
  const set = (rowId: string, o: ImportOverrides) => edit.mutate({ rowId, o });

  const firstOfGroup = useMemo(() => {
    const seen = new Set<string>();
    return rows.filter((r) => !r.skip && !seen.has(r.groupKey) && seen.add(r.groupKey));
  }, [rows]);
  const live = rows.filter((r) => !r.skip);
  const lists: Record<Exclude<Section, 'all'>, ImportPlan[]> = {
    matches: firstOfGroup.filter((r) => r.company.suggestId),
    newWon: firstOfGroup.filter((r) => r.company.mode === 'new' && live.some((x) => x.groupKey === r.groupKey && x.result === 'won')),
    values: live.filter((r) => r.result === 'won' && (r.valueUnconfirmed || r.mrr == null)),
    websites: firstOfGroup.filter((r) => r.company.mode === 'new' && live.some((x) => x.groupKey === r.groupKey && x.result === 'open')),
    skipped: rows.filter((r) => r.skip),
  };

  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Review: {batch.fileName}</h3><Badge variant="accent">Nothing saved yet</Badge></div>
      <div className="crm-import-stats">
        <Stat label="Deals to import" value={s.toImport} sub={`${s.open} open · ${s.won} won · ${s.lost} lost`} />
        <Stat label="Companies" value={s.newCompanies + s.matchedCompanies} sub={`${s.matchedCompanies} matched · ${s.newCompanies} new`} />
        <Stat label="Notes" value={s.notes.toLocaleString()} sub="dated timeline entries" />
        <Stat label="Follow-ups" value={s.followUps} sub="from the notes" />
      </div>
      {progress.aiFailed > 0 && (
        <p className="crm-callout" style={{ marginTop: 12 }}>
          AI couldn't read {progress.aiFailed} deal(s); they import without suggestions.{' '}
          <Button size="sm" variant="outline" onClick={() => retry.mutate()} disabled={retry.isPending}>Try again</Button>
        </p>
      )}
      {!batch.aiUsed && <p className="crm-callout" style={{ marginTop: 12 }}>AI is not configured, so follow-ups, lost reasons and contacts from the notes are not suggested. Contacts named in deal titles are still added.</p>}
      <DefaultOwner batch={batch} onDone={onChanged} />

      <div style={{ marginTop: 18 }}>
        <Tabs value={section} onChange={(v) => setSection(v as Section)} items={[
          { value: 'matches', label: 'Similar names', count: s.undecidedMatches || undefined },
          { value: 'newWon', label: 'New client companies', count: lists.newWon.length || undefined },
          { value: 'values', label: 'Values to confirm', count: lists.values.filter((r) => r.valueUnconfirmed).length || undefined },
          { value: 'websites', label: 'Open deal websites', count: s.openWithoutWebsite || undefined },
          { value: 'skipped', label: 'Skipped', count: s.skipped || undefined },
          { value: 'all', label: 'All deals' },
        ]} />
      </div>
      <div className="crm-import-section">
        {section === 'matches' && <Matches rows={lists.matches} set={set} />}
        {section === 'newWon' && <NewClients rows={lists.newWon} set={set} />}
        {section === 'values' && <Values rows={lists.values} set={set} />}
        {section === 'websites' && <Websites rows={lists.websites} set={set} />}
        {section === 'skipped' && <Skipped rows={lists.skipped} set={set} />}
        {section === 'all' && <AllDeals rows={rows} set={set} />}
      </div>
      <FormError error={edit.error ?? commit.error ?? retry.error ?? discard.error} />

      <div className="crm-import-foot">
        <Button variant="ghost" onClick={() => setConfirmDiscard(true)}>Discard</Button>
        <span className="crm-cell-sub">{s.undecidedMatches ? `${s.undecidedMatches} similar name(s) undecided — they import as new companies.` : ''}</span>
        <Button onClick={() => commit.mutate()} disabled={commit.isPending || !s.toImport}>
          {commit.isPending ? 'Starting…' : `Import ${s.toImport} deals`}
        </Button>
      </div>
      {confirmDiscard && (
        <Modal title="Discard this import?" onClose={() => setConfirmDiscard(false)} footer={<>
          <Button variant="ghost" onClick={() => setConfirmDiscard(false)}>Keep</Button>
          <Button variant="danger" onClick={() => discard.mutate()} disabled={discard.isPending}>Discard</Button>
        </>}>
          <p>The uploaded file and your review edits are deleted. Nothing in the CRM changes.</p>
        </Modal>
      )}
    </Card>
  );
}

type SetFn = (rowId: string, o: ImportOverrides) => void;

const Empty = ({ children }: { children: ReactNode }) => <p className="lead-none">{children}</p>;

function Matches({ rows, set }: { rows: ImportPlan[]; set: SetFn }) {
  if (!rows.length) return <Empty>No similar names to decide.</Empty>;
  return (
    <>
      <p className="crm-cell-muted">These names look like a company clientIQ already has, but not exactly. Decide each one — a wrong merge mixes two companies' history.</p>
      <table className="dash-table">
        <thead><tr><th>In the old CRM</th><th>Already in clientIQ</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowId}>
              <td><strong>{r.legacyCompany}</strong></td>
              <td>{r.company.suggestName}</td>
              <td className="crm-num">
                <div className="crm-import-choice">
                  <Button size="sm" variant={r.company.mode === 'existing' ? 'primary' : 'outline'} onClick={() => set(r.rowId, { useSuggested: true })}>Same company</Button>
                  <Button size="sm" variant={r.company.suggestDecided && r.company.mode === 'new' ? 'primary' : 'outline'} onClick={() => set(r.rowId, { useSuggested: false })}>Different</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// The name as it will be imported; for a confirmed match, the old CRM's name.
const rowCompanyName = (r: ImportPlan) => (r.company.mode === 'existing' ? r.company.existingName : r.company.name) ?? '(no name)';

function NewClients({ rows, set }: { rows: ImportPlan[]; set: SetFn }) {
  if (!rows.length) return <Empty>Every won deal matched a company clientIQ already has.</Empty>;
  const lifecycleOptions = LIFECYCLE_OPTIONS.filter((o) => o.value === 'client' || o.value === 'past_client');
  return (
    <>
      <p className="crm-cell-muted">Won deals for companies that aren't in clientIQ (not in tritonIQ's client list). Mark each as a current or past client.</p>
      <table className="dash-table">
        <thead><tr><th>Company</th><th>Won</th><th>Lifecycle</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowId}>
              <td><strong>{r.company.name}</strong></td>
              <td className="crm-cell-muted">{r.closedAt ?? '—'}</td>
              <td>
                <select className="crm-inline-select" value={r.company.lifecycle} onChange={(e) => set(r.rowId, { lifecycle: e.target.value as Lifecycle })}>
                  {lifecycleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Values({ rows, set }: { rows: ImportPlan[]; set: SetFn }) {
  if (!rows.length) return <Empty>No won values to confirm.</Empty>;
  return (
    <>
      <p className="crm-cell-muted">Won deals showing $500 or less — usually the old CRM's placeholder, not the retainer. Enter the real monthly value, or confirm it. Unconfirmed values import flagged on the deal.</p>
      <table className="dash-table">
        <thead><tr><th>Company</th><th>Closed</th><th>Monthly value</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowId}>
              <td><strong>{rowCompanyName(r)}</strong></td>
              <td className="crm-cell-muted">{r.closedAt ?? '—'}</td>
              <td><InlineInput value={r.mrr != null ? String(r.mrr) : ''} placeholder="e.g. 898" inputMode="decimal" onSave={(v) => set(r.rowId, { mrr: v || null })} /></td>
              <td className="crm-num">
                {r.valueUnconfirmed
                  ? <Button size="sm" variant="outline" onClick={() => set(r.rowId, { valueConfirmed: true })}>Confirm</Button>
                  : <span className="crm-cell-sub"><Icon name="check" size={12} /> Confirmed</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Websites({ rows, set }: { rows: ImportPlan[]; set: SetFn }) {
  if (!rows.length) return <Empty>No new companies with open deals.</Empty>;
  return (
    <>
      <p className="crm-cell-muted">Gmail capture links email to a company by its website domain. Add one for the companies you're actively working so their email lands on the timeline.</p>
      <table className="dash-table">
        <thead><tr><th>Company</th><th>Stage</th><th>Website</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowId}>
              <td><strong>{r.company.name}</strong></td>
              <td className="crm-cell-muted">{r.stageName}</td>
              <td><InlineInput value={r.company.website ?? ''} placeholder="example.com" onSave={(v) => set(r.rowId, { website: v || null })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Skipped({ rows, set }: { rows: ImportPlan[]; set: SetFn }) {
  if (!rows.length) return <Empty>Nothing skipped.</Empty>;
  return (
    <table className="dash-table">
      <thead><tr><th>Row</th><th>Deal</th><th>Why</th><th /></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.rowId}>
            <td className="crm-cell-muted">{r.rowNo}</td>
            <td>
              {r.company.name
                ? <strong>{r.company.name}</strong>
                : <InlineInput value="" placeholder="Company name" onSave={(v) => v && set(r.rowId, { companyName: v, skip: false })} />}
              <span className="crm-cell-sub">{r.title} · {r.result}</span>
            </td>
            <td className="crm-cell-muted">{r.skipReason}</td>
            <td className="crm-num">{!r.alreadyImported && r.company.name && <Button size="sm" variant="outline" onClick={() => set(r.rowId, { skip: false })}>Include</Button>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const RESULT_TABS = [{ value: 'open', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }];

function AllDeals({ rows, set }: { rows: ImportPlan[]; set: SetFn }) {
  const [result, setResult] = useState<'open' | 'won' | 'lost'>('open');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const { ownerOptions } = useMembers();
  const { data: reasons = [] } = useQuery({ queryKey: ['options', 'lost_reason'], queryFn: () => listOptions('lost_reason') });
  const list = rows.filter((r) => !r.skip && r.result === result);
  return (
    <>
      <Tabs items={RESULT_TABS.map((t) => ({ ...t, count: rows.filter((r) => !r.skip && r.result === t.value).length }))} value={result} onChange={(v) => setResult(v as typeof result)} />
      <table className="dash-table crm-import-table">
        <thead>
          <tr>
            <th>Company</th><th>Stage</th><th className="crm-num">Value</th>
            {result === 'lost' && <th>Lost reason</th>}
            {result !== 'won' && <th>Follow-up</th>}
            <th>Owner</th><th />
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
              <tr key={r.rowId}>
                <td>
                  <strong>{rowCompanyName(r)}</strong>
                  <span className="crm-cell-sub">
                    {r.company.mode === 'existing' ? 'Existing company' : `New · ${LIFECYCLE_LABEL[r.company.lifecycle]}`}
                    {r.contact?.firstName ? ` · ${[r.contact.firstName, r.contact.lastName].filter(Boolean).join(' ')}` : ''}
                  </span>
                </td>
                <td>{r.result === 'open' ? r.stageName : r.workingStageName ?? r.legacyStage}<span className="crm-cell-sub">{r.createdAt}{r.closedAt ? ` → ${r.closedAt}` : ''}</span></td>
                <td className="crm-num">{r.mrr != null ? `${money(r.mrr)}/mo` : r.strategyFee != null ? <span className="crm-cell-sub">{money(r.strategyFee)} fee</span> : '—'}</td>
                {result === 'lost' && (
                  <td>
                    <select className="crm-inline-select" value={r.lostReasonId ?? ''} onChange={(e) => set(r.rowId, { lostReasonId: e.target.value || null })}>
                      <option value="">Not recorded</option>
                      {reasons.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                    {r.lostReasonFrom === 'ai' && <span className="crm-cell-sub">AI suggestion</span>}
                  </td>
                )}
                {result !== 'won' && (
                  <td>
                    <input type="date" className="crm-inline-input" value={r.followUpAt ?? ''} aria-label="Follow-up date"
                      onChange={(e) => set(r.rowId, { followUpAt: e.target.value || null })} />
                    {r.followUpNote && <span className="crm-cell-sub" title={r.followUpNote}>{r.followUpFrom === 'ai' ? 'AI: ' : ''}{r.followUpNote}</span>}
                  </td>
                )}
                <td>
                  <select className="crm-inline-select" value={r.ownerUserId} onChange={(e) => set(r.rowId, { ownerUserId: e.target.value })}>
                    {ownerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {r.legacyOwner && r.legacyOwner !== r.ownerName && <span className="crm-cell-sub">was {r.legacyOwner}</span>}
                </td>
                <td className="crm-num">
                  <button type="button" className="lead-modal__x" aria-label="Show notes" title={`${r.noteCount} notes`} onClick={() => setOpenRow(openRow === r.rowId ? null : r.rowId)}>
                    <Icon name="file-text" size={15} />
                  </button>
                  <button type="button" className="lead-modal__x" aria-label="Skip this deal" title="Skip" onClick={() => set(r.rowId, { skip: true })}><Icon name="x" size={15} /></button>
                </td>
              </tr>
          ))}
        </tbody>
      </table>
      {list.map((r) => openRow === r.rowId && (
        <Modal key={r.rowId} title={rowCompanyName(r)} wide onClose={() => setOpenRow(null)}>
          <p className="crm-cell-sub">“{r.title}” · {r.legacyStage} · {r.noteCount} dated notes</p>
          {r.warnings.map((w) => <p key={w} className="crm-error">{w}</p>)}
          <p className="crm-timeline__note">{r.details.replace(/\s*,\s*,\s*/g, '\n') || 'No notes.'}</p>
        </Modal>
      ))}
      {!list.length && <Empty>No {result} deals in this file.</Empty>}
    </>
  );
}

/* Saves on Enter or blur when changed — one request per edit. */
function InlineInput({ value, onSave, placeholder, inputMode }: { value: string; onSave: (v: string) => void; placeholder?: string; inputMode?: 'decimal' }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => { if (v.trim() !== value) onSave(v.trim()); };
  return (
    <input className="crm-inline-input crm-inline-input--boxed" value={v} placeholder={placeholder} inputMode={inputMode}
      onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setV(value); }} />
  );
}

/* ---- after import ------------------------------------------------------------ */

function Finished({ data, onChanged }: { data: ImportDetail; onChanged: () => void }) {
  const { batch } = data;
  const [confirm, setConfirm] = useState(false);
  const undo = useMutation({ mutationFn: () => undoImport(batch.id), onSuccess: () => { setConfirm(false); onChanged(); } });
  const failed = (data.rows ?? []).filter((r) => r.state === 'failed');
  const r = batch.result;
  if (batch.status === 'undone') {
    return (
      <Card padding={false} className="crm-section">
        <div className="crm-section__head"><h3>{batch.fileName}</h3><Badge variant="outline">Undone</Badge></div>
        <p className="crm-cell-muted">This import was undone {relativeTime(batch.undoneAt)}. Upload the file again to start over.</p>
        {undo.data && <p className="crm-callout">Removed {undo.data.dealsRemoved} deals, {undo.data.companiesRemoved} companies and {undo.data.contactsRemoved} contacts.
          {undo.data.companiesKept + undo.data.contactsKept > 0 && ` Kept ${undo.data.companiesKept} companies and ${undo.data.contactsKept} contacts that are now used elsewhere.`}</p>}
      </Card>
    );
  }
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>{batch.fileName}</h3><Badge variant="success">Imported</Badge></div>
      {r && (
        <div className="crm-import-stats">
          <Stat label="Deals" value={r.deals} sub={r.failed ? `${r.failed} failed` : `${r.skipped} skipped`} />
          <Stat label="New companies" value={r.companies} />
          <Stat label="New contacts" value={r.contacts} />
          <Stat label="Notes" value={r.notes.toLocaleString()} />
        </div>
      )}
      <p className="crm-cell-muted" style={{ marginTop: 12 }}>
        Imported deals are tagged “Imported from the old CRM”. Follow-ups are on <Link to="/">Today</Link>; lost-only companies are under
        Companies → Past leads.
      </p>
      <OwnersCard batchId={batch.id} />
      {failed.length > 0 && (
        <div className="crm-callout">
          <strong>{failed.length} deal(s) could not be imported:</strong>
          <ul className="crm-import-notes">{failed.map((f) => <li key={f.rowId}>Row {f.rowNo} ({rowCompanyName(f)}): {f.error}</li>)}</ul>
        </div>
      )}
      <div className="crm-import-foot">
        <span />
        <span />
        <Button variant="outline" onClick={() => setConfirm(true)}>Undo this import</Button>
      </div>
      <FormError error={undo.error} />
      {confirm && (
        <Modal title="Undo this import?" onClose={() => setConfirm(false)} footer={<>
          <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => undo.mutate()} disabled={undo.isPending}>{undo.isPending ? 'Undoing…' : 'Undo import'}</Button>
        </>}>
          <p>Removes the {r?.deals ?? ''} imported deals with their notes and history — including anything added to those deals since — and the
            companies and contacts this import created. A company or contact that something else now uses (a lead, an email, a meeting, a Vault file)
            is kept. Companies that already existed are not touched.</p>
        </Modal>
      )}
    </Card>
  );
}

/* Imported deals grouped by their old CRM owner, each sent to a member in one
   step. Pre-filled by name; "Leave as is" for people who haven't joined yet —
   run it again after they do. Only deals still with the owner the import (or
   the last run) gave them move; a deal changed by hand stays put. */
function OwnersCard({ batchId }: { batchId: string }) {
  const qc = useQueryClient();
  const { ownerOptions } = useMembers();
  const { data: owners = [] } = useQuery({ queryKey: ['import', batchId, 'owners'], queryFn: () => getImportOwners(batchId) });
  const [pick, setPick] = useState<Record<string, string>>({});
  useEffect(() => {
    setPick(Object.fromEntries(owners.map((o) => [o.legacyOwner ?? '', o.suggestedUserId ?? ''])));
  }, [owners]);
  const apply = useMutation({
    mutationFn: () => reassignImportOwners(batchId, owners
      .filter((o) => pick[o.legacyOwner ?? ''])
      .map((o) => ({ legacyOwner: o.legacyOwner, userId: pick[o.legacyOwner ?? ''] }))),
    onSuccess: () => { for (const k of ['import', 'deals', 'companies', 'followUps', 'deal', 'company']) void qc.invalidateQueries({ queryKey: [k] }); },
  });
  const chosen = owners.filter((o) => pick[o.legacyOwner ?? '']);
  if (!owners.length) return null;
  return (
    <div className="crm-import-owners">
      <h4>Owners</h4>
      <p className="crm-cell-muted">
        Send each old owner's deals to a clientIQ member. People who haven't joined yet can stay as they are — come back after they sign in.
        Deals someone has already reassigned by hand are left alone. Notes a person signed link to their account when they're given their own name.
      </p>
      <table className="dash-table">
        <thead><tr><th>Owner in the old CRM</th><th className="crm-num">Deals</th><th>Held by now</th><th>Assign to</th></tr></thead>
        <tbody>
          {owners.map((o) => {
            const k = o.legacyOwner ?? '';
            return (
              <tr key={k}>
                <td><strong>{o.legacyOwner ?? 'No owner'}</strong>{o.notes > 0 && <span className="crm-cell-sub">{o.notes} signed notes</span>}</td>
                <td className="crm-num">{o.deals}{o.movable !== o.deals && <span className="crm-cell-sub">{o.movable} still movable</span>}</td>
                <td className="crm-cell-muted">{o.heldBy.map((h) => `${h.name} (${h.deals})`).join(', ')}</td>
                <td>
                  <select className="crm-inline-select" value={pick[k] ?? ''} onChange={(e) => setPick({ ...pick, [k]: e.target.value })}>
                    <option value="">Leave as is</option>
                    {ownerOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="crm-import-foot">
        <span className="crm-cell-sub">{apply.data ? `Moved ${apply.data.moved} deals${apply.data.notesLinked ? `, linked ${apply.data.notesLinked} notes` : ''}.` : ''}</span>
        <span />
        <Button onClick={() => apply.mutate()} disabled={!chosen.length || apply.isPending}>
          {apply.isPending ? 'Applying…' : `Apply to ${chosen.reduce((n, o) => n + o.movable, 0)} deals`}
        </Button>
      </div>
      <FormError error={apply.error} />
    </div>
  );
}
